/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TicketsFilter.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import CustomFilterTemplate from "../../../components/customComponentsV2/common/filter/CustomFilterTemplate";
import SortTemplate from "../../../components/customComponentsV2/common/Templates/SortTemplate";
import FilterTemplate from "../../../components/customComponentsV2/common/Templates/FilterTemplate";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomSearchFilterV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchFilterV2";
import {
  HandleApiActions,
  legalRequirements,
  userManagementApis,
} from "../../../redux/actions";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";
import { componentStyle } from "../../../config/appTheme";

const extractOwnerUserIds = (projectDetails) => {
  const userIds = new Set();
  const ownerDetails = projectDetails?.owner_details;

  if (Array.isArray(ownerDetails)) {
    for (const o of ownerDetails) {
      const id = typeof o === "object" ? o?.user_id ?? o?.id : o;
      if (id != null) userIds.add(Number(id));
    }
  } else if (ownerDetails && typeof ownerDetails === "object") {
    const id = ownerDetails?.user_id ?? ownerDetails?.id;
    if (id != null) userIds.add(Number(id));
  } else if (ownerDetails != null) {
    userIds.add(Number(ownerDetails));
  }

  const directOwnerId =
    projectDetails?.owner_id ??
    projectDetails?.owner_user_id ??
    projectDetails?.user_id;
  if (directOwnerId != null) userIds.add(Number(directOwnerId));

  return userIds;
};

const extractCoOwnerUserIds = (projectDetails) => {
  const userIds = new Set();
  const coOwnerRaw =
    projectDetails?.co_owners ?? projectDetails?.co_owner_details ?? [];

  let coOwnerList = [];
  if (Array.isArray(coOwnerRaw)) {
    coOwnerList = coOwnerRaw;
  } else if (typeof coOwnerRaw === "string" && coOwnerRaw.trim() !== "") {
    coOwnerList = coOwnerRaw.split(",");
  }

  for (const co of coOwnerList) {
    const id = typeof co === "object" ? co?.user_id ?? co?.id ?? co?.value : co;
    if (id != null) userIds.add(Number(id));
  }

  return userIds;
};

const extractProjectUserIds = (projectDetails) => {
  if (!projectDetails) return [];
  const ownerIds = extractOwnerUserIds(projectDetails);
  const coOwnerIds = extractCoOwnerUserIds(projectDetails);
  const combined = new Set([...ownerIds, ...coOwnerIds]);
  return Array.from(combined).filter((id) => !Number.isNaN(id) && id > 0);
};

const formatUserOption = (item) => ({
  label: `${item.fullname || ""} - ${item.email || ""}`,
  value: item.user_id,
  fullname: item.fullname,
  email: item.email,
});

const getOemUsers = (usersList, projectUserIds) => {
  if (!usersList?.length) return [];

  if (projectUserIds.length > 0) {
    const projectUsers = usersList.filter((oem) =>
      projectUserIds.includes(Number(oem?.user_id)),
    );
    if (projectUsers.length > 0) return projectUsers;
  }

  const roleFiltered = usersList.filter(
    (oem) =>
      oem?.role_details?.role_type === 2 || oem?.role_details?.role_type === 3,
  );
  return roleFiltered.length > 0 ? roleFiltered : usersList;
};

const getTechUsers = (usersList) => {
  if (!usersList?.length) return [];
  const techUsers = usersList.filter(
    (user) => user?.role_details?.role_type === 1,
  );
  return techUsers.length > 0 ? techUsers : usersList;
};

function TicketsFilter({ getFilterDetails, pageName = "" }) {
  const dispatch = useDispatch();
  const appMetaDetails = useSelector(
    (state) => state.settingsReducer?.metaData,
  );

  const UserDetails = useSelector((state) => state.userManagementReducer);
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const LSDetails = useSelector((state) => state.legalRequirementsReducer);
  const [filterByStatusData, setFilterByStatusData] = useState([]);
  const [inputData, setInputData] = useState([]);

  const [selectedSortObjBeforeClick, setSelectedSortObjBeforeClick] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });

  const [
    selectedFilterByStatusObjBeforeClick,
    setSelectedFilterByStatusObjBeforeClick,
  ] = useState([]);

  const [archiveStatus, setArchiveStatus] = useState("ACTIVE");
  const [userRadioValue, setUserRadioValue] = useState("req");
  const [filterUserList, setFilterUserList] = useState([]);
  const [selectedUserList, setSelectedUserList] = useState([]);
  const [legalInputList, setLegalInputList] = useState([]);
  const [filterLegalList, setFilterLegalList] = useState([]);
  const [selectedLegalList, setSelectedLegalList] = useState([]);

  const archiveStatusRadioData = [
    {
      value: "ARCHIVED",
      label: "Archive",
    },
    {
      value: "ACTIVE",
      label: "Active",
    },
  ];

  const radioData = [
    {
      value: "req",
      label: "Compliance Requestor",
    },
    {
      value: "rev",
      label: "Compliance Reviewer",
    },
  ];

  const handleUserSearch = useCallback((filteredData) => {
    setFilterUserList(filteredData);
  }, []);

  const handleLegalSearch = useCallback((filteredData) => {
    setFilterLegalList(filteredData);
  }, []);

  const getInputData = useCallback(() => inputData, [inputData]);

  useEffect(() => {
    let statusFilterTemp =
      appMetaDetails?.snow_ticket_status?.map((item) => {
        return {
          label: item?.key,
          value: item?.value,
        };
      }) || [];
    setFilterByStatusData(statusFilterTemp);
    if (pageName === "tickets") {
      setSelectedFilterByStatusObjBeforeClick(
        statusFilterTemp.filter((item) => Number(item.value) !== 101),
      );
    } else {
      setSelectedFilterByStatusObjBeforeClick(statusFilterTemp);
    }
  }, [appMetaDetails, pageName]);

  useEffect(() => {
    const getInputForUserBasedOnRadioValue = () => {
      if (userRadioValue === "req") {
        const projectUserIds = extractProjectUserIds(selectedProjectDetails);
        const oem_details = getOemUsers(UserDetails?.usersList, projectUserIds);
        return oem_details.map(formatUserOption);
      }
      if (userRadioValue === "rev") {
        const tech_details = getTechUsers(UserDetails?.usersList);
        return tech_details.map(formatUserOption);
      }
      if (userRadioValue === "sup") {
        return appMetaDetails?.usersList || [];
      }
      return [];
    };

    const userData = getInputForUserBasedOnRadioValue();
    setInputData(userData);
  }, [userRadioValue, UserDetails, selectedProjectDetails, appMetaDetails]);

  useEffect(() => {
    console.log("---LSDetails---", LSDetails);
    let legalInputListTemp =
      LSDetails?.legalStandardDetails?.length > 0
        ? LSDetails?.legalStandardDetails?.map((item) => {
            return {
              label: item?.standard_number,
              value: item?.ls_id,
              ...item,
            };
          })
        : [];
    setLegalInputList(legalInputListTemp);
  }, [LSDetails]);


  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...userManagementApis?.get_users_list,
        params: {
          page_size: 2000,
        },
      }),
    );
    dispatch(
      HandleApiActions({
        ...legalRequirements.get_legal_standard_details,
        params: {
          page_number: 1,
          page_size: 300,
        },
      }),
    );
  }, [dispatch]);

  return (
    <Box>
      <CustomFilterTemplate
        handleDiscard={() => {
          setSelectedSortObjBeforeClick({
            sortBy: "created_at",
            sortOrder: "DESC",
          });
          setSelectedFilterByStatusObjBeforeClick(filterByStatusData);
          setSelectedUserList([]);
          setUserRadioValue("req");
          setSelectedLegalList([]);
          setArchiveStatus("ACTIVE");
        }}
        handleClickApply={() => {
          getFilterDetails({
            sort_order: selectedSortObjBeforeClick.sortOrder,
            sort_column: selectedSortObjBeforeClick.sortBy,
            status: selectedFilterByStatusObjBeforeClick
              ?.map((e) => e.value)
              ?.join(","),
            archive_status: archiveStatus,
            ...(userRadioValue === "req" &&
              selectedUserList.length && {
                oem_users: selectedUserList.join(","),
              }),
            ...(userRadioValue === "rev" &&
              selectedUserList.length && {
                ts_users: selectedUserList.join(","),
              }),
            ...(selectedLegalList.length && {
              ls_id: selectedLegalList.join(","),
            }),
          });
        }}
        sections={[
          {
            heading: "Sorting",
            content: (
              <SortTemplate
                defaultValue={selectedSortObjBeforeClick}
                handleOnChange={(sortValue) => {
                  setSelectedSortObjBeforeClick(sortValue);
                }}
              />
            ),
          },
          {
            heading: "Filter By Status",
            content: (
              <Box>
                
                <FilterTemplate
                  defaultValue={selectedFilterByStatusObjBeforeClick}
                  handleOnChange={(sortValue) => {
                    setSelectedFilterByStatusObjBeforeClick(sortValue);
                  }}
                  data={filterByStatusData}
                />
                
              </Box>
            ),
          },
          {
            heading: "Filter By Archive Status",
            content: (
              <Box sx={{ mb: 1 }}>
                <CustomRadioButtonV2
                  name="archiveStatusRadio"
                  value={archiveStatus}
                  onChange={(e) => {
                    setArchiveStatus(e.target.value);
                  }}
                  data={archiveStatusRadioData}
                />
              </Box>
            ),
          },
          {
            heading: "Filter By Users",
            content: (
              <Box>
                <Box>
                  <CustomRadioButtonV2
                    value={userRadioValue}
                    onChange={(e) => {
                      setUserRadioValue(e.target.value);
                    }}
                    data={radioData}
                  />
                </Box>
                <Box sx={{ p: 0, mt: 1, mb: 1 }}>
                  <CustomSearchFilterV2
                    placeholder="Search"
                    searchKeys={["fullname", "email"]}
                    getDataOnSearch={handleUserSearch}
                    inputData={getInputData()}
                  />
                </Box>
                <Box
                  sx={{
                    minHeight: "290px",
                    maxHeight: "290px",
                    overflowY: "auto",
                    ...componentStyle.scroll,
                  }}
                >
                  {filterUserList?.length > 0
                    ? filterUserList?.map((item) => {
                        return (
                          <CustomToolTip key={item.value} title={item.label} type="component">
                            <CustomCheckBoxV2
                              label={
                                item.label.length > 20
                                  ? item.label.substring(0, 20) + "..."
                                  : item.label
                              }
                              value={selectedUserList.includes(item?.value)}
                              onChange={() => {
                                if (selectedUserList.includes(item?.value)) {
                                  setSelectedUserList(
                                    selectedUserList.filter(
                                      (id) => id !== item?.value,
                                    ),
                                  );
                                } else {
                                  setSelectedUserList([
                                    ...selectedUserList,
                                    item?.value,
                                  ]);
                                }
                              }}
                            />
                          </CustomToolTip>
                        );
                      })
                    : null}
                </Box>
              </Box>
            ),
          },
          {
            heading: "Filter By Legal",
            content: (
              <Box>
                <Box sx={{ mb: 1 }}>
                  <CustomSearchFilterV2
                    placeholder="Search"
                    searchKeys={["standard_number"]}
                    getDataOnSearch={handleLegalSearch}
                    inputData={legalInputList}
                  />
                </Box>
                <Box
                  sx={{
                    minHeight: "340px",
                    maxHeight: "340px",
                    overflowY: "auto",
                    ...componentStyle.scroll,
                  }}
                >
                  {filterLegalList?.length > 0
                    ? filterLegalList?.map((item) => {
                        return (
                          <CustomToolTip
                            key={item.value}
                            title={item.label.length > 15 ? item.label : ""}
                            type="component"
                          >
                            <CustomCheckBoxV2
                              label={
                                item.label.length > 15
                                  ? item.label.substring(0, 15) + "..."
                                  : item.label
                              }
                              value={selectedLegalList.includes(item?.value)}
                              onChange={() => {
                                if (selectedLegalList.includes(item?.value)) {
                                  setSelectedLegalList(
                                    selectedLegalList.filter(
                                      (id) => id !== item?.value,
                                    ),
                                  );
                                } else {
                                  setSelectedLegalList([
                                    ...selectedLegalList,
                                    item?.value,
                                  ]);
                                }
                              }}
                            />
                          </CustomToolTip>
                        );
                      })
                    : null}
                </Box>
              </Box>
            ),
          },
        ]}
      />
    </Box>
  );
}

TicketsFilter.propTypes = {
  getFilterDetails: PropTypes.func.isRequired,
  pageName: PropTypes.string,
};

export default TicketsFilter;
